import {
  AsYouType,
  CountryCode,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js/max"

import {
  CountryIdentifiers,
  getCountryPhoneData,
} from "@/utilities/localization"

// Formats a phone number for display. Numbers already in E.164 (e.g. "+1…") are
// shown internationally with their country code; bare national numbers (e.g. a
// theme's support line "3013661227") are parsed against defaultCountry and shown
// in national format
export function formatPhoneNumberForDisplay(
  phone: string,
  defaultCountry: CountryCode = "US"
) {
  if (!phone) return phone
  const hasCountryCode = phone.trim().startsWith("+")
  const parsed = parsePhoneNumberFromString(
    phone,
    hasCountryCode ? undefined : defaultCountry
  )
  if (!parsed) return phone
  return hasCountryCode ? parsed.formatInternational() : parsed.formatNational()
}

// Converts a phone number to E.164 format (e.g. "+12133734253") for AWS
// Cognito / API submission. Values coming from the country-select input are
// already E.164; defaultCountry covers raw/legacy input without a country code.
export function toE164(input: string, defaultCountry: CountryCode = "US") {
  if (!input) return ""
  const parsed = parsePhoneNumberFromString(input, defaultCountry)
  return parsed ? parsed.number : input
}

/*
  libphonenumber-js accurately detects a number's country, including territories
  that share a dial code (NANP +1, +44 crown dependencies, +358 Åland, etc.).
  Some of those territories we don't carry flag/country data for, so we map them
  to a parent country we DO support

  This is the place to manually address country-code exceptions: the library
  handles detection, and this table only remaps the result. Add/remove entries
  freely without touching any detection logic.
*/
const COUNTRY_CODE_OVERRIDES: Partial<Record<CountryCode, CountryIdentifiers>> =
  {
    // NANP (+1) territories we don't carry data for -> US
    AI: "US", // Anguilla
    AS: "US", // American Samoa
    BM: "US", // Bermuda
    GU: "US", // Guam
    MP: "US", // Northern Mariana Islands
    MS: "US", // Montserrat
    SX: "US", // Sint Maarten
    TC: "US", // Turks and Caicos Islands
    VI: "US", // U.S. Virgin Islands
    // +290 British Overseas Territories -> GB
    SH: "GB", // Saint Helena
    TA: "GB", // Tristan da Cunha
    // +262 French overseas territories -> FR
    RE: "FR", // Réunion
    YT: "FR", // Mayotte
    // +599 former Netherlands Antilles -> NL
    CW: "NL", // Curaçao
    BQ: "NL", // Caribbean Netherlands (Bonaire, Sint Eustatius, Saba)
    // +47 -> NO
    SJ: "NO", // Svalbard and Jan Mayen
  }

/*
  Resolves which country to preselect for a phone value. Uses the libphonenumber.js'
  detection, applies our manual overrides, then falls back to defaultCountry
  if we don't carry data for the detected country.
*/
export function detectCountry(
  value: string,
  defaultCountry: CountryIdentifiers
): CountryIdentifiers {
  const parsed = parsePhoneNumberFromString(value)
  const detected = parsed && parsed.country
  if (!detected) return defaultCountry

  const override = COUNTRY_CODE_OVERRIDES[detected]
  if (override) return override

  const data = getCountryPhoneData(detected)
  return data ? data.code : defaultCountry
}

// National calling code for one of our countries, e.g. "US" -> "1".
export function getCallingCode(country: CountryIdentifiers): string {
  return getCountryCallingCode(country as CountryCode)
}

// Progressive ("as you type") national formatting for a country, e.g.
// formatNationalNumber("2133734", "US") -> "(213) 373-4".
export function formatNationalNumber(
  digits: string,
  country: CountryIdentifiers
): string {
  return new AsYouType(country as CountryCode).input(digits)
}
